import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { widgetsAPI } from '../services/api';
import usePageTitle from '../hooks/usePageTitle';
import { capitalizePlatformName } from '../utils/platformNames';
import WidgetTypeModal from '../components/CreateWidget/WidgetTypeModal';

const Widgets = () => {
    usePageTitle('Widgets');
    const navigate = useNavigate();
    const [widgets, setWidgets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    // Load saved view mode from localStorage, default to 'grid'
    const [viewMode, setViewMode] = useState(() => {
        try {
            return localStorage.getItem('widgetsViewMode') || 'grid';
        } catch (error) {
            console.error('Error loading view mode from localStorage:', error);
            return 'grid';
        }
    }); // grid, list, table
    const [sortBy, setSortBy] = useState('dateCreated'); // dateCreated, lastModified, alphabetical
    const [sortOrder, setSortOrder] = useState('desc'); // asc, desc
    const [showEmbedModal, setShowEmbedModal] = useState(false);
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedWidget, setSelectedWidget] = useState(null);
    const [newWidgetName, setNewWidgetName] = useState('');
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [copySuccess, setCopySuccess] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showWidgetTypeModal, setShowWidgetTypeModal] = useState(false);

    // Handle widget type selection
    const handleWidgetTypeSelect = (type) => {
        if (type === 'review') {
            navigate('/create-widget');
        } else if (type === 'whatsapp') {
            navigate('/create-widget?type=whatsapp');
        } else if (type === 'appointment') {
            navigate('/create-widget?type=appointment');
        }
        setShowWidgetTypeModal(false);
    };

    useEffect(() => {
        fetchWidgets();
    }, []);

    // Handle view mode change and save to localStorage
    const handleViewModeChange = (newViewMode) => {
        try {
            setViewMode(newViewMode);
            localStorage.setItem('widgetsViewMode', newViewMode);
        } catch (error) {
            console.error('Error saving view mode to localStorage:', error);
            // Still update the state even if localStorage fails
            setViewMode(newViewMode);
        }
    };

    const fetchWidgets = async () => {
        try {
            setLoading(true);
            const response = await widgetsAPI.getWidgets();
            setWidgets(response.data.widgets || []);
        } catch (error) {
            console.error('Error fetching widgets:', error);
            setWidgets([]); // Set empty array on error to prevent filter issues
        } finally {
            setLoading(false);
        }
    };

    const handleEditWidget = (widgetId, e) => {
        e.stopPropagation();
        navigate(`/create-widget?edit=${widgetId}`);
    };

    const handleDeleteWidget = (widget, e) => {
        e.stopPropagation();
        setSelectedWidget(widget);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (deleteConfirmText.toLowerCase() !== 'delete') {
            return;
        }
        try {
            await widgetsAPI.deleteWidget(selectedWidget._id);
            setWidgets(widgets.filter(widget => widget._id !== selectedWidget._id));
            setShowDeleteModal(false);
            setSelectedWidget(null);
            setDeleteConfirmText('');

            showSuccess('Widget deleted successfully!');
        } catch (error) {
            console.error('Error deleting widget:', error);
            showError('Failed to delete widget. Please try again.');
        }
    };

    const handleToggleActive = async (widgetId, currentStatus, e) => {
        e.stopPropagation();
        try {
            await widgetsAPI.updateWidgetStatus(widgetId, !currentStatus);

            setWidgets(widgets.map(widget =>
                widget._id === widgetId
                    ? { ...widget, isActive: !currentStatus }
                    : widget
            ));

            showSuccess(`Widget ${!currentStatus ? 'enabled' : 'disabled'} successfully!`);
        } catch (error) {
            console.error('Error updating widget status:', error);
            showError('Failed to update widget status. Please try again.');
        }
    };

    const handleCopyEmbedCode = (widget, e) => {
        e.stopPropagation();
        setSelectedWidget(widget);
        setShowEmbedModal(true);
    };

    const handleShareAppointmentWidget = async (widget, e) => {
        e.stopPropagation();
        if (widget.type === 'appointment') {
            const baseUrl = process.env.REACT_APP_WIDGETS_URL || 'https://widgets.rannkly.com';
            const shareUrl = `${baseUrl}/widgets/embed/appointment/${widget._id}`;

            try {
                await navigator.clipboard.writeText(shareUrl);
                showSuccess('Share link copied to clipboard!');
            } catch (err) {
                console.error('Failed to copy share URL:', err);
                showError('Failed to copy share link. Please try again.');
            }
        }
    };

    const handleRenameWidget = (widget, e) => {
        e.stopPropagation();
        setSelectedWidget(widget);
        setNewWidgetName(widget.name);
        setShowRenameModal(true);
    };

    const handleRenameSubmit = async () => {
        try {
            await widgetsAPI.updateWidgetName(selectedWidget._id, newWidgetName);

            setWidgets(widgets.map(widget =>
                widget._id === selectedWidget._id
                    ? { ...widget, name: newWidgetName }
                    : widget
            ));
            setShowRenameModal(false);
            setSelectedWidget(null);
            setNewWidgetName('');

            showSuccess('Widget renamed successfully!');
        } catch (error) {
            console.error('Error renaming widget:', error);
            showError('Failed to rename widget. Please try again.');
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        setCopySuccess(true);
        setTimeout(() => {
            setCopySuccess(false);
            setShowEmbedModal(false);
        }, 1500);
    };

    const handleDuplicateWidget = async (widget, e) => {
        e.stopPropagation();
        try {
            const duplicatedWidget = {
                ...widget,
                name: `${widget.name} Copy`,
                _id: undefined,
                createdAt: undefined,
                updatedAt: undefined
            };
            const response = await widgetsAPI.createWidget(duplicatedWidget);
            setWidgets([...widgets, response.data]);

            showSuccess('Widget duplicated successfully!');
        } catch (error) {
            console.error('Error duplicating widget:', error);
            showError('Failed to duplicate widget. Please try again.');
        }
    };

    const getWidgetImage = (widget) => {
        // First priority: Use the template image if available
        if (widget?.template?.image) {
            return widget.template.image;
        }

        // Fallback: Use static mapping based on widget type
        const imageMap = {
            'reviews-carousel': '/review_widgets/Carousel Widget.png',
            'reviews-grid': '/review_widgets/Grid.png',
            'reviews-list': '/review_widgets/List.png',
            'reviews-badge': '/review_widgets/Badge.png',
            'reviews-floating': '/review_widgets/Floating Badge.png',
            'reviews-slider': '/review_widgets/Slider.png',
            'reviews-sidebar': '/review_widgets/Sidebar Widget.png',
            'reviews-masonry': '/review_widgets/Mansory.png',
            'reviews-simple-carousel': '/review_widgets/Simple Carousel.png',
            'reviews-dark-carousel': '/review_widgets/Dark Carousel.png',
            'reviews-dark-grid': '/review_widgets/Dark Grid.png',
            'reviews-dark-floating': '/review_widgets/Dark Floating Badge.png',
            // New template types
            'carousel-widget': '/review_widgets/Carousel Widget.png',
            'grid': '/review_widgets/Grid.png',
            'badge': '/review_widgets/Badge.png',
            'simple-carousel': '/review_widgets/Simple Carousel.png',
            'floating-badge': '/review_widgets/Floating Badge.png',
            'list': '/review_widgets/List.png',
            'mansory': '/review_widgets/Mansory.png',
            'sidebar-widget': '/review_widgets/Sidebar Widget.png',
            'slider': '/review_widgets/Slider.png',
            'dark-carousel': '/review_widgets/Dark Carousel.png',
            'dark-grid': '/review_widgets/Dark Grid.png',
            'dark-floating-badge': '/review_widgets/Dark Floating Badge.png',
        };
        return imageMap[widget?.type] || '/review_widgets/Grid.png';
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const getFilteredAndSortedWidgets = () => {
        // Ensure widgets is an array before filtering
        if (!Array.isArray(widgets)) {
            return [];
        }

        let filtered = widgets.filter(widget =>
            widget.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            widget.type.toLowerCase().includes(searchTerm.toLowerCase())
        );

        // Sort widgets
        filtered.sort((a, b) => {
            let comparison = 0;
            switch (sortBy) {
                case 'dateCreated':
                    comparison = new Date(a.createdAt) - new Date(b.createdAt);
                    break;
                case 'lastModified':
                    comparison = new Date(a.updatedAt || a.createdAt) - new Date(b.updatedAt || b.createdAt);
                    break;
                case 'alphabetical':
                    comparison = a.name.localeCompare(b.name);
                    break;
                default:
                    comparison = 0;
            }
            return sortOrder === 'desc' ? -comparison : comparison;
        });

        return filtered;
    };

    const filteredWidgets = getFilteredAndSortedWidgets();

    const showSuccess = (message) => {
        setSuccessMessage(message);
        setShowSuccessModal(true);
        setTimeout(() => {
            setShowSuccessModal(false);
            setSuccessMessage('');
        }, 3000);
    };

    const showError = (message) => {
        setErrorMessage(message);
        setShowErrorModal(true);
        setTimeout(() => {
            setShowErrorModal(false);
            setErrorMessage('');
        }, 5000);
    };

    // Enhanced Modal Components
    const EmbedCodeModal = () => (
        showEmbedModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 w-full max-w-3xl mx-4 border border-green-200 shadow-2xl">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center border border-green-200">
                                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="text-2xl font-bold text-gray-900">Install Your Widget</h3>
                                <p className="text-green-700 font-medium">Easy integration in just 2 steps!</p>
                            </div>
                        </div>
                        <button
                            onClick={() => setShowEmbedModal(false)}
                            className="text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-lg hover:bg-white/50"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Installation Steps */}
                    <div className="bg-white rounded-xl p-6 mb-6 border border-green-200 shadow-sm">
                        <div className="flex items-center space-x-3 mb-4">
                            <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white text-sm font-bold">1</div>
                            <h4 className="text-lg font-semibold text-gray-900">Copy the embed code</h4>
                        </div>
                        <div className="bg-gray-900 rounded-xl p-4 mb-4 relative group">
                            <code className="text-sm text-green-400 whitespace-pre-wrap break-all font-mono leading-relaxed">
                                {selectedWidget?.embedCode}
                            </code>
                            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                    onClick={() => copyToClipboard(selectedWidget?.embedCode)}
                                    className="bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white p-2 rounded-lg transition-colors text-xs"
                                    disabled={copySuccess}
                                >
                                    {copySuccess ? 'Copied!' : 'Copy'}
                                </button>
                            </div>
                        </div>

                        <div className="flex items-center space-x-3 mb-4">
                            <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white text-sm font-bold">2</div>
                            <h4 className="text-lg font-semibold text-gray-900">Paste it into your website</h4>
                        </div>
                        <p className="text-gray-600 ml-11">
                            Add the code to your HTML where you want the widget to appear. It will load automatically!
                        </p>
                    </div>

                    {/* Success Message */}
                    {copySuccess && (
                        <div className="bg-green-100 border border-green-300 rounded-xl p-4 mb-6 flex items-center space-x-3">
                            <svg className="w-6 h-6 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <div>
                                <p className="text-green-800 font-semibold">Code copied successfully!</p>
                                <p className="text-green-700 text-sm">You can now paste it into your website&apos;s HTML.</p>
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex justify-between items-center">
                        <button
                            onClick={() => setShowEmbedModal(false)}
                            className="px-6 py-3 text-gray-600 hover:text-gray-800 font-medium transition-colors"
                        >
                            Close
                        </button>
                        <button
                            onClick={() => copyToClipboard(selectedWidget?.embedCode)}
                            className="bg-green-600 text-white px-8 py-3 rounded-xl hover:bg-green-700 flex items-center space-x-2 disabled:opacity-50 font-medium transition-all transform hover:scale-105"
                            disabled={copySuccess}
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            <span>{copySuccess ? '✓ Copied!' : 'Copy Code'}</span>
                        </button>
                    </div>
                </div>
            </div>
        )
    );

    // Success Modal
    const SuccessModal = () => (
        showSuccessModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl border border-green-200">
                    <div className="flex items-center space-x-4 mb-4">
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Success!</h3>
                            <p className="text-green-700 text-sm">{successMessage}</p>
                        </div>
                    </div>
                </div>
            </div>
        )
    );

    // Error Modal
    const ErrorModal = () => (
        showErrorModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl border border-red-200">
                    <div className="flex items-center space-x-4 mb-4">
                        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Error</h3>
                            <p className="text-red-700 text-sm">{errorMessage}</p>
                        </div>
                    </div>
                    <div className="flex justify-end">
                        <button
                            onClick={() => setShowErrorModal(false)}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        )
    );

    const RenameModal = () => (
        showRenameModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">Rename Widget</h3>
                        <button
                            onClick={() => {
                                setShowRenameModal(false);
                                setSelectedWidget(null);
                                setNewWidgetName('');
                            }}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Widget Name
                        </label>
                        <input
                            type="text"
                            value={newWidgetName}
                            onChange={(e) => setNewWidgetName(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Enter widget name"
                            autoFocus
                            onKeyPress={(e) => {
                                if (e.key === 'Enter' && newWidgetName.trim()) {
                                    handleRenameSubmit();
                                }
                            }}
                        />
                    </div>
                    <div className="flex justify-end space-x-3">
                        <button
                            onClick={() => {
                                setShowRenameModal(false);
                                setSelectedWidget(null);
                                setNewWidgetName('');
                            }}
                            className="px-4 py-2 text-gray-600 hover:text-gray-800"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleRenameSubmit}
                            disabled={!newWidgetName.trim()}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Rename
                        </button>
                    </div>
                </div>
            </div>
        )
    );

    const DeleteModal = () => (
        showDeleteModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-red-600">Delete Widget</h3>
                        <button
                            onClick={() => {
                                setShowDeleteModal(false);
                                setSelectedWidget(null);
                                setDeleteConfirmText('');
                            }}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                    <div className="mb-4">
                        <p className="text-gray-600 mb-4">
                            Are you sure you want to delete <strong>&ldquo;{selectedWidget?.name}&rdquo;</strong>?
                            This action cannot be undone.
                        </p>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Type <strong>delete</strong> to confirm:
                        </label>
                        <input
                            type="text"
                            value={deleteConfirmText}
                            onChange={(e) => setDeleteConfirmText(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                            placeholder="delete"
                            autoFocus
                        />
                    </div>
                    <div className="flex justify-end space-x-3">
                        <button
                            onClick={() => {
                                setShowDeleteModal(false);
                                setSelectedWidget(null);
                                setDeleteConfirmText('');
                            }}
                            className="px-4 py-2 text-gray-600 hover:text-gray-800"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleDeleteConfirm}
                            disabled={deleteConfirmText.toLowerCase() !== 'delete'}
                            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Delete Widget
                        </button>
                    </div>
                </div>
            </div>
        )
    );

    // Widget Card Component
    const WidgetCard = ({ widget }) => {
        const [showCardDropdown, setShowCardDropdown] = useState(false);
        const cardDropdownRef = useRef(null);

        // Close dropdown when clicking outside
        useEffect(() => {
            const handleClickOutside = (event) => {
                if (cardDropdownRef.current && !cardDropdownRef.current.contains(event.target)) {
                    setShowCardDropdown(false);
                }
            };

            if (showCardDropdown) {
                document.addEventListener('mousedown', handleClickOutside);
            }

            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }, [showCardDropdown]);

        const handleToggleCardDropdown = (e) => {
            e.stopPropagation();
            setShowCardDropdown(!showCardDropdown);
        };

        return (
            <div className="bg-white rounded-xl border border-gray-100 hover:border-blue-200 hover:shadow-md transition-all duration-200 overflow-visible group w-full max-w-sm relative flex flex-col h-full">
                {/* Widget Preview */}
                <div className="aspect-[4/3] bg-gray-50 relative overflow-hidden group/preview">
                    {widget.type === 'appointment' ? (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-500 to-purple-600 relative">
                            <img
                                src="/calendar_icon.svg"
                                alt="Calendar"
                                className="w-16 h-16 filter brightness-0 invert"
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    const parent = e.target.parentElement;
                                    if (!parent.querySelector('.fallback-calendar')) {
                                        const fallbackIcon = document.createElement('div');
                                        fallbackIcon.className = 'fallback-calendar text-white text-6xl';
                                        fallbackIcon.innerHTML = '📅';
                                        parent.appendChild(fallbackIcon);
                                    }
                                }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-br from-transparent to-black/5"></div>
                        </div>
                    ) : widget.type === 'whatsapp' ? (
                        <div
                            className="w-full h-full flex items-center justify-center relative"
                            style={{ backgroundColor: '#128478' }}
                        >
                            <img
                                src="https://d7zc54wk8qed4.cloudfront.net/assets/icons/whatsapp.svg"
                                alt="WhatsApp"
                                className="w-12 h-12 object-contain"
                            />
                            <div className="absolute inset-0 bg-gradient-to-br from-transparent to-black/5"></div>
                        </div>
                    ) : (
                        <img
                            src={getWidgetImage(widget)}
                            alt={widget.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                                e.target.src = '/review_widgets/Grid.png';
                            }}
                        />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                    {/* Status Badge */}
                    <div className={`absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-medium ${widget.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-600'
                        }`}>
                        {widget.isActive ? 'Enabled' : 'Disabled'}
                    </div>

                    {/* Menu Button */}
                    <div className="absolute top-3 left-3 z-40">
                        <button
                            onClick={handleToggleCardDropdown}
                            className="p-2 text-gray-600 hover:text-gray-800 transition-all duration-300 rounded-full hover:bg-white/20 hover:backdrop-blur-sm hover:shadow-sm"
                        >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Dropdown Menu - Positioned at card level to avoid clipping */}
                {showCardDropdown && (
                    <div
                        ref={cardDropdownRef}
                        className="absolute left-3 top-14 w-48 bg-white border border-gray-200 rounded-xl shadow-2xl py-2"
                        style={{ zIndex: 99999 }}>
                        <button
                            onClick={(e) => {
                                handleCopyEmbedCode(widget, e);
                                setShowCardDropdown(false);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            <span>Embed Code</span>
                        </button>
                        {widget.type === 'appointment' && (
                            <button
                                onClick={(e) => {
                                    handleShareAppointmentWidget(widget, e);
                                    setShowCardDropdown(false);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-purple-600 hover:bg-purple-50 flex items-center space-x-2"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                                </svg>
                                <span>Share Link</span>
                            </button>
                        )}
                        <div className="border-t border-gray-100 my-1" />
                        <button
                            onClick={(e) => {
                                handleToggleActive(widget._id, widget.isActive, e);
                                setShowCardDropdown(false);
                            }}
                            className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center space-x-2 ${widget.isActive ? 'text-orange-600 hover:bg-orange-50' : 'text-green-600 hover:bg-green-50'
                                }`}
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                    d={widget.isActive ? "M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" : "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"}
                                />
                            </svg>
                            <span>{widget.isActive ? 'Disable' : 'Enable'}</span>
                        </button>
                        <button
                            onClick={(e) => {
                                handleDuplicateWidget(widget, e);
                                setShowCardDropdown(false);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            <span>Duplicate</span>
                        </button>
                        <button
                            onClick={(e) => {
                                handleRenameWidget(widget, e);
                                setShowCardDropdown(false);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            <span>Rename widget</span>
                        </button>
                        <div className="border-t border-gray-100 my-1" />
                        <button
                            onClick={(e) => {
                                handleDeleteWidget(widget, e);
                                setShowCardDropdown(false);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            <span>Delete Widget</span>
                        </button>
                    </div>
                )}

                {/* Widget Info */}
                <div className="p-4 flex-grow flex flex-col">
                    <h3 className="font-semibold text-gray-900 mb-1 line-clamp-1 text-blue-600 text-sm">
                        {widget.name}
                    </h3>
                    <p className="text-xs text-gray-500 mb-3">
                        Created {formatDate(widget.createdAt)}
                    </p>

                    {/* Location and Platform Info - Flexible container */}
                    <div className="flex-grow space-y-1.5 mb-3">
                        {/* Locations */}
                        {widget.locations && widget.locations.length > 0 && (
                            <div className="flex items-start space-x-2">
                                <svg className="w-3 h-3 text-gray-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <div className="flex flex-wrap gap-1">
                                    {widget.locations.slice(0, 2).map((location, index) => (
                                        <span key={index} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                                            {location.name}
                                        </span>
                                    ))}
                                    {widget.locations.length > 2 && (
                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-50 text-gray-600">
                                            +{widget.locations.length - 2}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Platforms */}
                        {widget.platformData && widget.platformData.length > 0 && (
                            <div className="flex items-start space-x-2">
                                <svg className="w-3 h-3 text-gray-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                </svg>
                                <div className="flex flex-wrap gap-1">
                                    {widget.platformData.slice(0, 2).map((platform, index) => (
                                        <div key={index} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 space-x-1">
                                            {platform.iconUrl && (
                                                <img
                                                    src={platform.iconUrl}
                                                    alt={platform.name}
                                                    className="w-2.5 h-2.5 rounded-sm"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                    }}
                                                />
                                            )}
                                            <span>{capitalizePlatformName(platform.name)}</span>
                                        </div>
                                    ))}
                                    {widget.platformData.length > 2 && (
                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-50 text-gray-600">
                                            +{widget.platformData.length - 2}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Buttons - Fixed at bottom */}
                    <div className="flex items-center justify-between mt-auto">
                        <button
                            onClick={(e) => handleEditWidget(widget._id, e)}
                            className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded-md hover:bg-gray-200 transition-colors text-xs font-medium"
                        >
                            Edit
                        </button>
                        <button
                            onClick={(e) => handleCopyEmbedCode(widget, e)}
                            className="bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700 transition-colors text-xs font-medium"
                        >
                            Install
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // List View Component
    const WidgetList = ({ widget }) => (
        <div className="bg-white border border-gray-100 rounded-xl p-6 hover:shadow-md transition-all duration-200 flex items-center space-x-6 relative overflow-visible group">
            <div className="w-24 h-16 bg-gray-50 rounded-lg overflow-hidden flex-shrink-0">
                {widget.type === 'whatsapp' ? (
                    <div
                        className="w-full h-full flex items-center justify-center"
                        style={{ backgroundColor: '#128478' }}
                    >
                        <img
                            src="https://d7zc54wk8qed4.cloudfront.net/assets/icons/whatsapp.svg"
                            alt="WhatsApp"
                            className="w-6 h-6 object-contain"
                        />
                    </div>
                ) : (
                    <img
                        src={getWidgetImage(widget)}
                        alt={widget.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                            e.target.src = '/review_widgets/Grid.png';
                        }}
                    />
                )}
            </div>

            <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1 text-blue-600">{widget.name}</h3>
                <p className="text-sm text-gray-500 mb-2">Created {formatDate(widget.createdAt)}</p>

                {/* Location and Platform Info */}
                <div className="space-y-1">
                    {/* Locations */}
                    {widget.locations && widget.locations.length > 0 && (
                        <div className="flex items-center space-x-2">
                            <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <div className="flex flex-wrap gap-1">
                                {widget.locations.slice(0, 2).map((location, index) => (
                                    <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                                        {location.name}
                                    </span>
                                ))}
                                {widget.locations.length > 2 && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-50 text-gray-600">
                                        +{widget.locations.length - 2} more
                                    </span>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Platforms */}
                    {widget.platformData && widget.platformData.length > 0 && (
                        <div className="flex items-center space-x-2">
                            <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                            </svg>
                            <div className="flex flex-wrap gap-1">
                                {widget.platformData.slice(0, 3).map((platform, index) => (
                                    <div key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 space-x-1">
                                        {platform.iconUrl && (
                                            <img
                                                src={platform.iconUrl}
                                                alt={platform.name}
                                                className="w-3 h-3 rounded-sm"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                }}
                                            />
                                        )}
                                        <span>{capitalizePlatformName(platform.name)}</span>
                                    </div>
                                ))}
                                {widget.platformData.length > 3 && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-50 text-gray-600">
                                        +{widget.platformData.length - 3} more
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="flex items-center space-x-4">
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${widget.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-600'
                    }`}>
                    {widget.isActive ? 'Enabled' : 'Disabled'}
                </div>

                {/* Three Dots Menu */}
                <div className="relative group/menu">
                    <button className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
                        <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                        </svg>
                    </button>

                    {/* Dropdown Menu */}
                    <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-gray-200 rounded-xl shadow-2xl py-2 opacity-0 invisible group-hover/menu:opacity-100 group-hover/menu:visible transition-all duration-200"
                        style={{ zIndex: 9999 }}>
                        <button
                            onClick={(e) => handleCopyEmbedCode(widget, e)}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            <span>Embed Code</span>
                        </button>
                        {widget.type === 'appointment' && (
                            <button
                                onClick={(e) => handleShareAppointmentWidget(widget, e)}
                                className="w-full text-left px-4 py-2 text-sm text-purple-600 hover:bg-purple-50 flex items-center space-x-2"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                                </svg>
                                <span>Share Link</span>
                            </button>
                        )}
                        <div className="border-t border-gray-100 my-1" />
                        <button
                            onClick={(e) => handleToggleActive(widget._id, widget.isActive, e)}
                            className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center space-x-2 ${widget.isActive ? 'text-orange-600 hover:bg-orange-50' : 'text-green-600 hover:bg-green-50'
                                }`}
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                    d={widget.isActive ? "M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" : "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"}
                                />
                            </svg>
                            <span>{widget.isActive ? 'Disable' : 'Enable'}</span>
                        </button>
                        <button
                            onClick={(e) => handleDuplicateWidget(widget, e)}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            <span>Duplicate</span>
                        </button>
                        <button
                            onClick={(e) => handleRenameWidget(widget, e)}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            <span>Rename widget</span>
                        </button>
                        <div className="border-t border-gray-100 my-1" />
                        <button
                            onClick={(e) => handleDeleteWidget(widget, e)}
                            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            <span>Delete Widget</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    // Table View Component
    const WidgetTable = () => (
        <div className="bg-white border border-gray-100 rounded-xl overflow-visible">
            <div className="overflow-x-auto overflow-y-visible">
                <table className="w-full">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="text-left py-4 px-6 font-medium text-gray-700">Widget</th>
                            <th className="text-left py-4 px-6 font-medium text-gray-700">Locations</th>
                            <th className="text-left py-4 px-6 font-medium text-gray-700">Platforms</th>
                            <th className="text-left py-4 px-6 font-medium text-gray-700">Status</th>
                            <th className="text-left py-4 px-6 font-medium text-gray-700">Created</th>
                            <th className="text-right py-4 px-6 font-medium text-gray-700 w-16">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {filteredWidgets.map((widget) => (
                            <tr key={widget._id} className="hover:bg-gray-50 relative overflow-visible group">
                                <td className="py-4 px-6">
                                    <div className="flex items-center space-x-3">
                                        <div className="w-12 h-8 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                                            {widget.type === 'whatsapp' ? (
                                                <div
                                                    className="w-full h-full flex items-center justify-center"
                                                    style={{ backgroundColor: '#128478' }}
                                                >
                                                    <img
                                                        src="https://d7zc54wk8qed4.cloudfront.net/assets/icons/whatsapp.svg"
                                                        alt="WhatsApp"
                                                        className="w-4 h-4 object-contain"
                                                    />
                                                </div>
                                            ) : (
                                                <img
                                                    src={getWidgetImage(widget)}
                                                    alt={widget.name}
                                                    className="w-full h-full object-cover"
                                                    onError={(e) => {
                                                        e.target.src = '/review_widgets/Grid.png';
                                                    }}
                                                />
                                            )}
                                        </div>
                                        <span className="font-medium text-gray-900 text-blue-600">{widget.name}</span>
                                    </div>
                                </td>
                                <td className="py-4 px-6">
                                    {widget.locations && widget.locations.length > 0 ? (
                                        <div className="flex flex-wrap gap-1">
                                            {widget.locations.slice(0, 2).map((location, index) => (
                                                <span key={index} className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-50 text-blue-700">
                                                    {location.name}
                                                </span>
                                            ))}
                                            {widget.locations.length > 2 && (
                                                <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-50 text-gray-600">
                                                    +{widget.locations.length - 2}
                                                </span>
                                            )}
                                        </div>
                                    ) : (
                                        <span className="text-sm text-gray-400">No locations</span>
                                    )}
                                </td>
                                <td className="py-4 px-6">
                                    {widget.platformData && widget.platformData.length > 0 ? (
                                        <div className="flex flex-wrap gap-1">
                                            {widget.platformData.slice(0, 2).map((platform, index) => (
                                                <div key={index} className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-50 text-green-700 space-x-1">
                                                    {platform.iconUrl && (
                                                        <img
                                                            src={platform.iconUrl}
                                                            alt={platform.name}
                                                            className="w-3 h-3 rounded-sm"
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                            }}
                                                        />
                                                    )}
                                                    <span>{capitalizePlatformName(platform.name)}</span>
                                                </div>
                                            ))}
                                            {widget.platformData.length > 2 && (
                                                <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-50 text-gray-600">
                                                    +{widget.platformData.length - 2}
                                                </span>
                                            )}
                                        </div>
                                    ) : (
                                        <span className="text-sm text-gray-400">No platforms</span>
                                    )}
                                </td>
                                <td className="py-4 px-6">
                                    <div className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${widget.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-600'
                                        }`}>
                                        {widget.isActive ? 'Enabled' : 'Disabled'}
                                    </div>
                                </td>
                                <td className="py-4 px-6 text-sm text-gray-600">
                                    {formatDate(widget.createdAt)}
                                </td>
                                <td className="py-4 px-6">
                                    {/* Three Dots Menu */}
                                    <div className="flex justify-end">
                                        <div className="relative group/menu">
                                            <button className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
                                                <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                                                    <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                                                </svg>
                                            </button>

                                            {/* Dropdown Menu */}
                                            <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-gray-200 rounded-xl shadow-2xl py-2 opacity-0 invisible group-hover/menu:opacity-100 group-hover/menu:visible transition-all duration-200"
                                                style={{ zIndex: 9999 }}>
                                                <button
                                                    onClick={(e) => handleCopyEmbedCode(widget, e)}
                                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                    </svg>
                                                    <span>Embed Code</span>
                                                </button>
                                                {widget.type === 'appointment' && (
                                                    <button
                                                        onClick={(e) => handleShareAppointmentWidget(widget, e)}
                                                        className="w-full text-left px-4 py-2 text-sm text-purple-600 hover:bg-purple-50 flex items-center space-x-2"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                                                        </svg>
                                                        <span>Share Link</span>
                                                    </button>
                                                )}
                                                <div className="border-t border-gray-100 my-1" />
                                                <button
                                                    onClick={(e) => handleToggleActive(widget._id, widget.isActive, e)}
                                                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center space-x-2 ${widget.isActive ? 'text-orange-600 hover:bg-orange-50' : 'text-green-600 hover:bg-green-50'
                                                        }`}
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                            d={widget.isActive ? "M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" : "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"}
                                                        />
                                                    </svg>
                                                    <span>{widget.isActive ? 'Disable' : 'Enable'}</span>
                                                </button>
                                                <button
                                                    onClick={(e) => handleDuplicateWidget(widget, e)}
                                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                    </svg>
                                                    <span>Duplicate</span>
                                                </button>
                                                <button
                                                    onClick={(e) => handleRenameWidget(widget, e)}
                                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                    </svg>
                                                    <span>Rename widget</span>
                                                </button>
                                                <div className="border-t border-gray-100 my-1" />
                                                <button
                                                    onClick={(e) => handleDeleteWidget(widget, e)}
                                                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                    <span>Delete Widget</span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );

    // Skeleton Loading Components
    const WidgetCardSkeleton = () => (
        <div className="bg-white rounded-xl border border-gray-100 p-0 animate-pulse overflow-hidden w-full max-w-sm flex flex-col h-full">
            {/* Widget Preview Skeleton */}
            <div className="aspect-[4/3] bg-gray-200"></div>
            {/* Widget Info Skeleton */}
            <div className="p-4 flex-grow flex flex-col">
                <div className="h-5 bg-gray-300 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-3"></div>

                {/* Location and Platform Info Skeleton - Flexible container */}
                <div className="flex-grow space-y-2 mb-3">
                    <div className="flex items-start space-x-2">
                        <div className="w-4 h-4 bg-gray-200 rounded mt-0.5 flex-shrink-0"></div>
                        <div className="flex flex-wrap gap-1">
                            <div className="h-6 bg-gray-200 rounded-md w-16"></div>
                            <div className="h-6 bg-gray-200 rounded-md w-12"></div>
                        </div>
                    </div>
                    <div className="flex items-start space-x-2">
                        <div className="w-4 h-4 bg-gray-200 rounded mt-0.5 flex-shrink-0"></div>
                        <div className="flex flex-wrap gap-1">
                            <div className="h-6 bg-gray-200 rounded-md w-20"></div>
                            <div className="h-6 bg-gray-200 rounded-md w-16"></div>
                            <div className="h-6 bg-gray-200 rounded-md w-14"></div>
                        </div>
                    </div>
                </div>

                {/* Buttons Skeleton - Fixed at bottom */}
                <div className="flex items-center justify-between mt-auto">
                    <div className="h-8 bg-gray-200 rounded w-20"></div>
                    <div className="h-8 bg-gray-200 rounded w-20"></div>
                </div>
            </div>
        </div>
    );

    if (loading) {
        return (
            <DashboardLayout>
                <div className="space-y-6">
                    {/* Header Skeleton */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-pulse">
                        <div>
                            <div className="h-4 bg-gray-200 rounded w-32"></div>
                        </div>
                        <div className="h-12 bg-gray-200 rounded-xl w-40"></div>
                    </div>

                    {/* Controls Skeleton */}
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 bg-white p-4 rounded-xl border border-gray-100 animate-pulse">
                        <div className="flex items-center space-x-4">
                            <div className="h-10 bg-gray-200 rounded-lg w-48"></div>
                            <div className="h-10 bg-gray-200 rounded-lg w-40"></div>
                        </div>
                        <div className="h-10 bg-gray-200 rounded-lg w-80"></div>
                    </div>

                    {/* Widgets Display Skeleton - Default to Grid View */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 auto-rows-fr">
                        {[1, 2, 3, 4, 5, 6].map((i) => (
                            <div key={i} className="flex justify-center h-full">
                                <WidgetCardSkeleton />
                            </div>
                        ))}
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6 overflow-visible">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <p className="text-gray-600">
                            {filteredWidgets.length} widget{filteredWidgets.length !== 1 ? 's' : ''}
                        </p>
                    </div>
                    <button
                        onClick={() => setShowWidgetTypeModal(true)}
                        className="bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors inline-flex items-center space-x-2 shadow-sm"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        <span>Create Widget</span>
                    </button>
                </div>

                {/* Controls */}
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 bg-white p-4 rounded-xl border border-gray-100">
                    {/* Left Side - View Controls */}
                    <div className="flex items-center space-x-4">
                        {/* View Mode Selector */}
                        <div className="flex bg-gray-100 rounded-lg p-1">
                            <button
                                onClick={() => handleViewModeChange('grid')}
                                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center space-x-2 ${viewMode === 'grid' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                                    }`}
                            >
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                                </svg>
                                <span>Grid</span>
                            </button>
                            <button
                                onClick={() => handleViewModeChange('list')}
                                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center space-x-2 ${viewMode === 'list' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                                    }`}
                            >
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                                </svg>
                                <span>List</span>
                            </button>
                            <button
                                onClick={() => handleViewModeChange('table')}
                                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center space-x-2 ${viewMode === 'table' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                                    }`}
                            >
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M5 4a1 1 0 00-1 1v10a1 1 0 001 1h10a1 1 0 001-1V5a1 1 0 00-1-1H5zM3 5a3 3 0 013-3h8a3 3 0 013 3v10a3 3 0 01-3 3H6a3 3 0 01-3-3V5z" clipRule="evenodd" />
                                    <path fillRule="evenodd" d="M9 9a1 1 0 000 2h6a1 1 0 100-2H9zM9 13a1 1 0 100 2h6a1 1 0 100-2H9zM5 9a1 1 0 011-1h.01a1 1 0 110 2H6a1 1 0 01-1-1zM5 13a1 1 0 011-1h.01a1 1 0 110 2H6a1 1 0 01-1-1z" clipRule="evenodd" />
                                </svg>
                                <span>Table</span>
                            </button>
                        </div>

                        {/* Sort Dropdown */}
                        <div className="relative">
                            <select
                                value={`${sortBy}-${sortOrder}`}
                                onChange={(e) => {
                                    const [newSortBy, newSortOrder] = e.target.value.split('-');
                                    setSortBy(newSortBy);
                                    setSortOrder(newSortOrder);
                                }}
                                className="bg-white border border-gray-200 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none pr-8 cursor-pointer"
                                style={{ backgroundImage: 'none' }}
                            >
                                <option value="dateCreated-desc">Date Created (Newest)</option>
                                <option value="dateCreated-asc">Date Created (Oldest)</option>
                                <option value="lastModified-desc">Last Modified (Newest)</option>
                                <option value="lastModified-asc">Last Modified (Oldest)</option>
                                <option value="alphabetical-asc">Alphabetical (A-Z)</option>
                                <option value="alphabetical-desc">Alphabetical (Z-A)</option>
                            </select>
                            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </div>
                        </div>
                    </div>

                    {/* Right Side - Search */}
                    <div className="flex items-center space-x-4">
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Search widgets..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full md:w-80 pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Widgets Display */}
                {filteredWidgets.length === 0 ? (
                    <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
                        {widgets.length === 0 ? (
                            <div className="max-w-md mx-auto">
                                <div className="mb-6">
                                    <img
                                        src="/empty_widget_placeholder.gif"
                                        alt="Empty widget placeholder"
                                        className="mx-auto w-48 h-48 object-contain"
                                    />
                                </div>
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                                    Your first widget is almost here!
                                </h2>
                                <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                                    Create beautiful widgets using our ready-made templates.
                                </p>
                                <button
                                    onClick={() => setShowWidgetTypeModal(true)}
                                    className="bg-blue-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-blue-700 transition-all duration-200 inline-flex items-center space-x-2 shadow-lg hover:shadow-xl"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                    </svg>
                                    <span>Create Widget</span>
                                </button>
                            </div>
                        ) : (
                            <div>
                                <div className="text-6xl mb-4">🔍</div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    No widgets match your search
                                </h3>
                                <p className="text-gray-600 mb-6">
                                    Try adjusting your search criteria to find your widgets.
                                </p>
                            </div>
                        )}
                    </div>
                ) : (
                    <>
                        {viewMode === 'grid' && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 auto-rows-fr overflow-visible">
                                {filteredWidgets.map((widget) => (
                                    <div key={widget._id} className="flex justify-center overflow-visible relative h-full">
                                        <WidgetCard widget={widget} />
                                    </div>
                                ))}
                            </div>
                        )}

                        {viewMode === 'list' && (
                            <div className="space-y-4 overflow-visible">
                                {filteredWidgets.map((widget) => (
                                    <WidgetList key={widget._id} widget={widget} />
                                ))}
                            </div>
                        )}

                        {viewMode === 'table' && <WidgetTable />}
                    </>
                )}
            </div>

            {/* Modals */}
            <EmbedCodeModal />
            <SuccessModal />
            <ErrorModal />
            <RenameModal />
            <DeleteModal />

            {/* Widget Type Selection Modal */}
            <WidgetTypeModal
                isOpen={showWidgetTypeModal}
                onClose={() => setShowWidgetTypeModal(false)}
                onSelectType={handleWidgetTypeSelect}
            />
        </DashboardLayout>
    );
};

export default Widgets; 